/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import * as React from 'react';
import { action, observable, computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
//                     Imported app modules/assets                      //
//----------------------------------------------------------------------//

import AppBar from '@material-ui/core/AppBar';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import { ElectroTooltip } from 'components/ExtendedUiElements/customized';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import * as statuses from 'utils/statuses';

import styles from './FinderColumns.module.scss';
import Tooltip from '@material-ui/core/Tooltip';

//----------------------------------------------------------------------//
//                       Exported React component                       //
//----------------------------------------------------------------------//

export type FinderColumnProps = {
  finderColumn: {
    columnTitle: string,
    columnData: Array<*>,
    itemData?: Object,
    itemType?: string,
    field?: string,
    field2?: string,
    columnTitleCount?: string,
    archiveAction?: Function,
    unarchiveAction?: Function,
    displayCount?: Boolean,
    archiveItem?: boolean,
    actionForm?: () => void,
    approveItem?: boolean,
    editItem?: boolean,
    searchList?: boolean,
  },
  expandable?: Boolean,
  placeholder?: string,
};

export type FinderColumnInjectedProps = $PropertyType<FinderColumnProps, 'finderColumn'> &
  TranslatorProps;

const finderColumns: Function = (WrappedComponent: React.ComponentType<*>) => {
  @translate()
  @observer
  class FinderColumns extends ComponentWithInjectedProps<
    FinderColumnProps,
    {},
    FinderColumnInjectedProps
  > {
    static defaultProps = {
      columnTitle: '',
      archiveItem: false,
      approveItem: false,
      editItem: false,
      searchList: false,
    };

    @observable search = false;

    @observable filter = '';

    @action filterQuery = (item: Object) => {
      const field = this.props.finderColumn.field || 'name';
      const field2 = this.props.finderColumn.field2;

      const stringToSearch = typeof field === 'function' ? field(item) : item[field];
      const stringToSearch2 = typeof field2 === 'function' ? field2(item) : item[field2];

      return (
        stringToSearch.toLowerCase().indexOf(this.filter.toLowerCase()) > -1 ||
        (field2 &&
          stringToSearch2 &&
          !Array.isArray(toJS(stringToSearch2)) &&
          stringToSearch2.toLowerCase().indexOf(this.filter.toLowerCase()) > -1) ||
        (field2 &&
          stringToSearch2 &&
          Array.isArray(toJS(stringToSearch2)) &&
          this.searchInArray(stringToSearch2))
      );
    };

    searchInArray(searchArr: Array<string>) {
      return searchArr.reduce(
        (found, item) => item.toLowerCase().indexOf(this.filter.toLowerCase()) > -1 || found,
        false
      );
    }

    @action mapStatus = (item: Object) => {
      const columnData: Object = {};

      Object.keys(item).forEach((key: string) => {
        columnData[key] = item[key].filter(this.filterQuery);
      });

      return columnData;
    };

    @computed get filteredList(): ?Array<*> {
      if (this.filter.length > 0) {
        return this.props.expandable
          ? this.props.finderColumn.columnData.map(this.mapStatus)
          : this.props.finderColumn.columnData.filter(this.filterQuery);
      }

      return this.props.finderColumn.columnData;
    }

    displayExpandableCount = (item: Object) => {
      let count: number = 0;

      Object.keys(item).forEach((key: string) => {
        count += item[key].length;
      });

      return count;
    };

    handleEditItem = (itemData: Object) => {};

    handleSearchOpen = () => {
      this.search = true;
    };

    handleSearchClose = () => {
      this.search = false;
      this.filter = '';
    };

    handleFilterList = (ev: SyntheticInputEvent<*>) => {
      this.filter = ev.target.value;
    };

    renderButton(tooltip: ?string, icon: string, func?: Function, linkTo?: Object) {
      const renderIconButton: React.Node = (
        <IconButton onClick={func}>
          <svg className={icon === '#icon-close' ? styles['icon-close'] : styles.icon}>
            <use xlinkHref={icon} />
          </svg>
        </IconButton>
      );

      const renderLink: React.Node = linkTo ? (
        <Link to={linkTo}>{renderIconButton}</Link>
      ) : (
        renderIconButton
      );

      return tooltip ? <ElectroTooltip title={tooltip}>{renderLink}</ElectroTooltip> : renderLink;
    }

    renderSearch(): React.Node {
      return (
        <Input
          id="input-with-icon-adornment"
          placeholder={this.props.placeholder || this.props.t('general:placeholders.search')}
          autoFocus={true}
          disableUnderline={true}
          fullWidth={true}
          onChange={this.handleFilterList}
          startAdornment={
            <InputAdornment position="start" className={styles['column-input-adornment']}>
              <svg className={styles.icon}>
                <use xlinkHref="#icon-search" />
              </svg>
            </InputAdornment>
          }
          endAdornment={this.renderButton(null, '#icon-close', this.handleSearchClose)}
        />
      );
    }

    renderHeader(): React.Node {
      const {
        columnTitle,
        archiveItem,
        archiveAction,
        unarchiveAction,
        approveItem,
        displayCount,
        editItem,
        searchList,
        columnData,
        itemData,
        itemType,
      } = this.props.finderColumn;

      const { t, tooltip } = this.props;

      const hasData = Boolean(
        columnData && columnData[0] && columnData.length && typeof columnData !== 'string'
      );

      const isSearchableClass: string = searchList && hasData ? styles['is-searchable'] : '';

      const isApproved = Boolean(itemData && statuses.getCode(itemData.status) === 'approved');
      const isArchived = Boolean(itemData && statuses.getCode(itemData.status) === 'archived');

      let columnTitleCount: string = '';

      if (displayCount && hasData) {
        columnTitleCount =
          '(' +
          (this.props.expandable
            ? // flow-disable-next-line TODO: Figure out what is trying to be achieved here
              columnData.map(this.displayExpandableCount)
            : columnData.length) +
          ')';
      }

      return (
        <React.Fragment>
          <Tooltip title={tooltip ? tooltip : ''}>
            <Typography
              variant="subheading"
              className={`${styles['column-title-text']} ${isSearchableClass}`}
              onClick={searchList && this.handleSearchOpen}
            >
              {`${columnTitle} ${columnTitleCount}`}
            </Typography>
          </Tooltip>
          {archiveItem &&
            isApproved &&
            this.renderButton(t('general:actions.archive'), '#icon-archive', archiveAction)}
          {archiveItem &&
            isArchived &&
            this.renderButton(t('general:actions.unarchive'), '#icon-unarchive', unarchiveAction)}
          {editItem &&
            itemData &&
            itemType &&
            this.renderButton(t('general:actions.edit'), '#icon-edit', undefined, {
              pathname: `${itemType}/edit`,
              state: { itemData },
            })}
          {approveItem &&
            itemData &&
            this.renderButton(t('general:actions.approve'), '#icon-approve', undefined)}
          {searchList && hasData && this.renderButton(null, '#icon-search', this.handleSearchOpen)}
        </React.Fragment>
      );
    }

    render(): React.Node {
      const { columnData, itemData } = this.props.finderColumn;

      const hasData: boolean = Boolean((columnData && columnData.length) || itemData);
      const isDisabledClass: string = hasData === false ? styles['is-disabled'] : '';

      return (
        <Grid item={true} xs={true} className={`${styles['finder-column']} ${isDisabledClass}`}>
          <Paper square={true} className={styles['stretch-paper']} elevation={0}>
            <AppBar
              position="static"
              color="default"
              elevation={0}
              className={styles['column-title']}
            >
              <Toolbar variant="dense" disableGutters={true}>
                {this.search && hasData ? this.renderSearch() : this.renderHeader()}
              </Toolbar>
            </AppBar>
            <WrappedComponent {...this.props} columnData={this.filteredList} />
          </Paper>
        </Grid>
      );
    }
  }

  return FinderColumns;
};

export default finderColumns;
